import { IconButton, joinNavItemGroups, NavItem, NavItemAccessor, Text } from '@billy/design-system'
import type { TFunction } from 'i18next'

import {
  productUnitCategoryToTranslationKey,
  productUnitSelectCategories,
} from '../../../constants/productUnitCategories'
import type { ConfiguredProductUnit } from '../../../types/productUnit'
import { getProductUnitFullName } from '../../../utils/getProductUnitFullName'
import { groupProductUnitsByCategory } from '../../../utils/groupProductUnitsByCategory'

const RECENT_GROUP_ID = 'recent'

const getGroup = (id: string, title: string, items: NavItem[]): NavItem[] => {
  if (!items.length) {
    return []
  }

  const headerItem: NavItem = {
    accessor: NavItemAccessor.Header,
    children: (
      <Text colorVariant="discreet" weight="medium">
        {title}
      </Text>
    ),
    id: `header-${id}`,
    value: '',
  }

  return [headerItem, ...items.map((item) => ({ ...item, accessorGroupId: headerItem.id }))]
}

const getUnitItem = (unit: ConfiguredProductUnit, t: TFunction): NavItem => {
  const fullName = getProductUnitFullName(unit, t)

  return {
    children: unit.name,
    id: unit.name,
    rightDecoration: fullName ? <Text colorVariant="discreet">{fullName}</Text> : undefined,
    searchKeywords: fullName ? [unit.name, fullName] : [unit.name],
    value: unit.name,
  }
}

const getRecentUnitItem = (unit: ConfiguredProductUnit, onRemove: (unit: string) => void, t: TFunction): NavItem => ({
  ...getUnitItem(unit, t),
  iconLeft: 'clock',
  id: `${RECENT_GROUP_ID}-${unit.name}`,
  rightDecoration: (
    <IconButton
      aria-label={t('products.units.recently_used.remove')}
      icon="x"
      onClick={(event) => {
        event.stopPropagation()
        onRemove(unit.name)
      }}
    />
  ),
})

interface GetProductUnitItemsOptions {
  onRemoveRecentUnit: (unit: string) => void
  recentUnits: ConfiguredProductUnit[]
  t: TFunction
  units: ConfiguredProductUnit[]
}

export const getProductUnitItems = (params: GetProductUnitItemsOptions) => {
  const { onRemoveRecentUnit, recentUnits, t, units } = params

  return joinNavItemGroups([
    getGroup(
      RECENT_GROUP_ID,
      t('products.units.category.recently_used'),
      recentUnits.map((unit) => getRecentUnitItem(unit, onRemoveRecentUnit, t)),
    ),
    ...groupProductUnitsByCategory(units, productUnitSelectCategories).map(({ category, units: categoryUnits }) =>
      getGroup(
        category,
        t(productUnitCategoryToTranslationKey[category]),
        categoryUnits.map((unit) => getUnitItem(unit, t)),
      ),
    ),
  ])
}
